/**
 * SMS Consent page - publicly accessible consent document.
 * Required for toll-free number verification and A2P messaging compliance.
 */

import { useState } from 'react';
import { makeStyles, tokens, Text, Input, Checkbox, Button, Spinner } from '@fluentui/react-components';
import { Link } from 'react-router-dom';

const useStyles = makeStyles({
    wrapper: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflowY: 'auto',
        backgroundColor: tokens.colorNeutralBackground1,
    },
    container: {
        maxWidth: '800px',
        margin: '0 auto',
        padding: '40px 20px',
        fontFamily: tokens.fontFamilyBase,
        lineHeight: '1.6',
        color: tokens.colorNeutralForeground1,
    },
    title: {
        fontSize: tokens.fontSizeHero800,
        fontWeight: tokens.fontWeightBold,
        marginBottom: '8px',
    },
    lastUpdated: {
        color: tokens.colorNeutralForeground3,
        marginBottom: '32px',
    },
    section: {
        marginBottom: '24px',
    },
    sectionTitle: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: tokens.fontWeightSemibold,
        marginBottom: '12px',
        marginTop: '24px',
    },
    paragraph: {
        marginBottom: '12px',
    },
    list: {
        marginLeft: '24px',
        marginBottom: '12px',
    },
    listItem: {
        marginBottom: '8px',
    },
    highlight: {
        backgroundColor: tokens.colorNeutralBackground3,
        padding: '16px',
        borderRadius: '8px',
        marginBottom: '16px',
    },
    highlightTitle: {
        fontWeight: tokens.fontWeightSemibold,
        marginBottom: '8px',
    },
    link: {
        color: tokens.colorBrandForeground1,
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    contact: {
        marginTop: '32px',
        paddingTop: '24px',
        borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    // Signup form styles
    signupForm: {
        backgroundColor: tokens.colorNeutralBackground3,
        border: `2px solid ${tokens.colorBrandBackground}`,
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '32px',
    },
    formTitle: {
        fontSize: tokens.fontSizeBase600,
        fontWeight: tokens.fontWeightSemibold,
        marginBottom: '16px',
        color: tokens.colorNeutralForeground1,
    },
    formField: {
        marginBottom: '16px',
    },
    formLabel: {
        display: 'block',
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        marginBottom: '6px',
        color: tokens.colorNeutralForeground1,
    },
    phoneInput: {
        width: '100%',
        maxWidth: '300px',
    },
    consentCheckbox: {
        alignItems: 'flex-start',
    },
    consentText: {
        fontSize: tokens.fontSizeBase200,
        lineHeight: '1.5',
        color: tokens.colorNeutralForeground2,
    },
    submitButton: {
        marginTop: '16px',
    },
    successMessage: {
        backgroundColor: tokens.colorPaletteGreenBackground1,
        border: `1px solid ${tokens.colorPaletteGreenBorder1}`,
        borderRadius: '8px',
        padding: '16px',
        marginTop: '16px',
    },
    successText: {
        color: tokens.colorPaletteGreenForeground1,
        fontWeight: tokens.fontWeightSemibold,
    },
    errorMessage: {
        backgroundColor: tokens.colorPaletteRedBackground1,
        border: `1px solid ${tokens.colorPaletteRedBorder1}`,
        borderRadius: '8px',
        padding: '16px',
        marginTop: '16px',
    },
    errorText: {
        color: tokens.colorPaletteRedForeground1,
    },
});

export function SMSConsentPage(): JSX.Element {
    const styles = useStyles();
    const [phoneNumber, setPhoneNumber] = useState('');
    const [consentChecked, setConsentChecked] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');

    const formatPhoneNumber = (value: string): string => {
        // Remove all non-digits
        const digits = value.replace(/\D/g, '');

        // Format as (XXX) XXX-XXXX
        if (digits.length <= 3) {
            return digits;
        } else if (digits.length <= 6) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        } else {
            return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
        }
    };

    const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const formatted = formatPhoneNumber(event.target.value);
        setPhoneNumber(formatted);
        // Clear any previous status when user starts typing
        if (submitStatus !== 'idle') {
            setSubmitStatus('idle');
        }
    };

    const handleSubmit = async (event: React.FormEvent): Promise<void> => {
        event.preventDefault();

        // Validate phone number (should have 10 digits)
        const digits = phoneNumber.replace(/\D/g, '');
        if (digits.length !== 10) {
            setSubmitStatus('error');
            setErrorMessage('Please enter a valid 10-digit phone number.');
            return;
        }

        if (!consentChecked) {
            setSubmitStatus('error');
            setErrorMessage('Please check the consent box to continue.');
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus('idle');

        try {
            // TODO: Replace with actual API call to store consent
            // For now, simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));

            setSubmitStatus('success');
            setPhoneNumber('');
            setConsentChecked(false);
        } catch {
            setSubmitStatus('error');
            setErrorMessage('An error occurred. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const isFormValid = phoneNumber.replace(/\D/g, '').length === 10 && consentChecked;

    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>
                <h1 className={styles.title}>SMS Terms & Consent</h1>
                <Text className={styles.lastUpdated}>Last updated: December 24, 2024</Text>

                {/* SMS Consent Signup Form */}
                <div className={styles.signupForm} id="sms-signup-form">
                    <h2 className={styles.formTitle}>Sign Up for SMS Notifications</h2>
                    <form onSubmit={handleSubmit}>
                        <div className={styles.formField}>
                            <label className={styles.formLabel} htmlFor="phone-number">
                                Mobile Phone Number
                            </label>
                            <Input
                                id="phone-number"
                                type="tel"
                                placeholder="(555) 555-5555"
                                value={phoneNumber}
                                onChange={handlePhoneChange}
                                className={styles.phoneInput}
                                disabled={isSubmitting}
                            />
                        </div>

                        <div className={styles.formField}>
                            <Checkbox
                                checked={consentChecked}
                                onChange={(_, data) => setConsentChecked(data.checked === true)}
                                disabled={isSubmitting}
                                className={styles.consentCheckbox}
                                label={
                                    <span className={styles.consentText}>
                                        By submitting this form, you consent to receive security credential
                                        text messages from IRPilot (MacGregor Consulting LLC) at the number
                                        provided. Msg &amp; data rates may apply. Msg frequency varies.
                                        Unsubscribe at any time by replying STOP. Reply HELP for help.
                                        Phone numbers are not shared with third parties for marketing purposes.{' '}
                                        <Link to="/privacy" className={styles.link}>Privacy Policy</Link>
                                        {' '}&amp;{' '}
                                        <Link to="/terms" className={styles.link}>Terms</Link>.
                                    </span>
                                }
                            />
                        </div>

                        <Button
                            type="submit"
                            appearance="primary"
                            disabled={!isFormValid || isSubmitting}
                            className={styles.submitButton}
                        >
                            {isSubmitting ? <Spinner size="tiny" /> : 'Submit'}
                        </Button>

                        {submitStatus === 'success' && (
                            <div className={styles.successMessage}>
                                <Text className={styles.successText}>
                                    Thank you! Your consent has been recorded. You may now receive
                                    security credential messages at this number.
                                </Text>
                            </div>
                        )}

                        {submitStatus === 'error' && (
                            <div className={styles.errorMessage}>
                                <Text className={styles.errorText}>{errorMessage}</Text>
                            </div>
                        )}
                    </form>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>About Our SMS Service</h2>
                    <p className={styles.paragraph}>
                        IRPilot, operated by MacGregor Consulting LLC, uses SMS text messaging to deliver
                        secure, one-time authentication credentials called Temporary Access Pass (TAP) codes.
                        These codes enable passwordless sign-in to Microsoft 365 accounts during incident
                        response operations.
                    </p>
                </div>

                <div className={styles.highlight}>
                    <p className={styles.highlightTitle}>Key Information</p>
                    <ul className={styles.list}>
                        <li className={styles.listItem}>
                            <strong>Message Type:</strong> Transactional security messages only (not marketing)
                        </li>
                        <li className={styles.listItem}>
                            <strong>Frequency:</strong> 1-2 messages per account setup or credential reset
                        </li>
                        <li className={styles.listItem}>
                            <strong>Opt-Out:</strong> Reply STOP to any message to opt out
                        </li>
                        <li className={styles.listItem}>
                            <strong>Help:</strong> Reply HELP for assistance
                        </li>
                        <li className={styles.listItem}>
                            <strong>Rates:</strong> Message and data rates may apply
                        </li>
                    </ul>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>How Consent Is Obtained</h2>
                    <p className={styles.paragraph}>
                        SMS messages are sent only when an authorized IT administrator provisions a user
                        account and provides a phone number for credential delivery. By providing your phone
                        number to your organization&apos;s IT administrator for this purpose, you consent to
                        receive transactional SMS messages from IRPilot containing your login credentials.
                    </p>
                    <p className={styles.paragraph}>
                        Your consent is specific to receiving:
                    </p>
                    <ul className={styles.list}>
                        <li className={styles.listItem}>
                            One-time Temporary Access Pass (TAP) codes for Microsoft 365 sign-in
                        </li>
                        <li className={styles.listItem}>
                            Password reset or account recovery credentials when requested by your administrator
                        </li>
                    </ul>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>What Messages You Will Receive</h2>
                    <p className={styles.paragraph}>
                        Messages will contain your one-time login credentials in the following format:
                    </p>
                    <div className={styles.highlight}>
                        <p className={styles.paragraph} style={{ fontFamily: 'monospace', marginBottom: 0 }}>
                            Your IRPilot access code is: XXXXXXXX<br />
                            This code expires in 60 minutes.<br />
                            Reply STOP to opt out.
                        </p>
                    </div>
                    <p className={styles.paragraph}>
                        We will never send marketing, promotional, or advertising messages. All messages
                        are transactional and directly related to your account access.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>Message Frequency</h2>
                    <p className={styles.paragraph}>
                        Message frequency depends on your organization&apos;s use of IRPilot:
                    </p>
                    <ul className={styles.list}>
                        <li className={styles.listItem}>
                            <strong>Initial setup:</strong> 1 message when your account is created
                        </li>
                        <li className={styles.listItem}>
                            <strong>Credential reset:</strong> 1 message each time your administrator
                            generates new credentials for you
                        </li>
                    </ul>
                    <p className={styles.paragraph}>
                        Most users receive only 1-2 messages total. Messages are only sent when explicitly
                        initiated by your organization&apos;s IT administrator.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>How to Opt Out</h2>
                    <p className={styles.paragraph}>
                        You can opt out of SMS messages at any time by:
                    </p>
                    <ul className={styles.list}>
                        <li className={styles.listItem}>
                            <strong>Replying STOP</strong> to any message you receive from IRPilot
                        </li>
                        <li className={styles.listItem}>
                            Contacting your organization&apos;s IT administrator to remove your phone number
                        </li>
                        <li className={styles.listItem}>
                            Contacting us directly at the email address below
                        </li>
                    </ul>
                    <p className={styles.paragraph}>
                        After opting out, you will receive a confirmation message and no further SMS
                        messages will be sent. Note that opting out may affect your ability to receive
                        login credentials via SMS; alternative delivery methods may be available through
                        your IT administrator.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>Getting Help</h2>
                    <p className={styles.paragraph}>
                        If you need assistance with SMS messages from IRPilot:
                    </p>
                    <ul className={styles.list}>
                        <li className={styles.listItem}>
                            Reply <strong>HELP</strong> to any message for assistance
                        </li>
                        <li className={styles.listItem}>
                            Contact your organization&apos;s IT administrator
                        </li>
                        <li className={styles.listItem}>
                            Email us at connect@macgregor.consulting
                        </li>
                    </ul>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>Privacy & Data Protection</h2>
                    <p className={styles.paragraph}>
                        Your phone number and message history are protected according to our{' '}
                        <Link to="/privacy" className={styles.link}>Privacy Policy</Link>. Key protections include:
                    </p>
                    <ul className={styles.list}>
                        <li className={styles.listItem}>
                            Phone numbers are stored only while your user account is active
                        </li>
                        <li className={styles.listItem}>
                            We do not sell, rent, or share phone numbers with third parties for marketing
                        </li>
                        <li className={styles.listItem}>
                            Message content is transmitted securely and not stored after delivery
                        </li>
                        <li className={styles.listItem}>
                            Phone numbers are deleted when your account is removed from the system
                        </li>
                    </ul>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>Carrier Information</h2>
                    <p className={styles.paragraph}>
                        Messages are sent via Azure Communication Services. Carriers are not liable for
                        delayed or undelivered messages. Message and data rates may apply depending on
                        your mobile carrier and plan.
                    </p>
                    <p className={styles.paragraph}>
                        Supported carriers include all major US carriers. For carrier-specific questions
                        about messaging rates, please contact your mobile carrier.
                    </p>
                </div>

                <div className={styles.contact}>
                    <h2 className={styles.sectionTitle}>Contact Us</h2>
                    <p className={styles.paragraph}>
                        If you have questions about our SMS messaging practices, please contact us:
                    </p>
                    <p className={styles.paragraph}>
                        <strong>MacGregor Consulting LLC</strong><br />
                        Email: connect@macgregor.consulting
                    </p>
                    <p className={styles.paragraph} style={{ marginTop: '16px' }}>
                        For privacy-related inquiries, please see our{' '}
                        <Link to="/privacy" className={styles.link}>Privacy Policy</Link>.
                    </p>
                </div>
            </div>
        </div>
    );
}
